export const TABLE = "Metadata";
export const PK = "system";
export const RK = "platform";

export type ComponentState = "managed" | "external";
export interface ImageStampEntry { tag: string; digest: string; state: ComponentState }
export interface PersonaStampEntry { treeSha: string; foundryVersion: string }

/** Outbound-email state for this install: the operator's INTENT plus the ACS identity
 *  that serves it.
 *
 *  Recording intent here — rather than inferring it from whether the executor happens
 *  to carry `M8T_ACS_*` — is what makes the capability durable. Before this existed,
 *  email lived only in the argv of `m8t azure-exec deploy --enable-email`, so the ezra
 *  rename redeployed the executor without it and the capability silently vanished for
 *  six weeks. Intent and wiring being separately readable is also what lets a reader
 *  tell "the operator turned this off" apart from "a redeploy dropped it" — those two
 *  produced the same message before, which is why nobody noticed.
 *
 *  `acsEndpoint`/`acsSender` are AUTHORITATIVE. ACS resource names used to be derived
 *  as `acs-${agentName}`, which is precisely the derivation the rename broke; recording
 *  the resolved values takes name derivation off the runtime path for good. */
export interface EmailFeatureStamp {
  enabled: boolean;
  /** ACS data-plane endpoint, e.g. `https://acs-x.unitedstates.communication.azure.com/`.
   *  Read from the resource — the region infix cannot be constructed. */
  acsEndpoint: string;
  /** Managed-domain sender, e.g. `DoNotReply@<guid>.azurecomm.net`. */
  acsSender: string;
}

export interface InstalledStamp {
  schemaVersion: 1;
  platformVersion: string;
  previousPlatformVersion: string | null;
  updatedAt: string;
  lastResult: "success" | "partial" | "failed";
  cli: string;
  /** How this stamp came to exist. Absent on every stamp written before install-time
   *  seeding existed (all of which were converge-written). Diagnostic only — no reader
   *  branches on it. */
  origin?: "install-seed" | "converge" | "cd";
  components: {
    gateway: ImageStampEntry;
    codingAgent: ImageStampEntry;
    azureExecutor: ImageStampEntry;
    personas: Record<string, PersonaStampEntry>;
    infra: { treeSha: string };
    /** Additive: coarse install-level record of the last seed-delivery pass.
     *  Absent on older stamps. The per-repo `.m8t/seed-manifest.json` markers are
     *  authoritative — this only gates drift detection + status display. */
    brainSeeds?: { seedRev: string };
  };
  lastError?: { component: string; message: string };
  /** Additive: opt-in platform capabilities. Absent on every stamp written before this
   *  field existed — and absence must always read as OFF (see `isEmailEnabled`). */
  features?: {
    email?: EmailFeatureStamp;
  };
}

/** Is outbound email enabled on this install?
 *
 *  🔴 Absence reads as OFF, and so does anything that is not exactly `true`. This
 *  asymmetry is deliberate and load-bearing: email is default-ON, but "default-on" is
 *  applied as an explicit WRITE at provision time, never inferred by a reader. If a
 *  reader treated absent as on, `rg-m8t-run-prod` — a public-facing install whose stamp
 *  has no `features` key, converging every five minutes — would arm itself. Because the
 *  default lives in the writer, prod stays off today, after any `blankStamp()` rebuild,
 *  and under any CLI that predates this field.
 *
 *  Accepts `null`/`undefined` because `readStamp` returns `InstalledStamp | null`: an
 *  unreadable or missing stamp must not read as enabled. */
export function isEmailEnabled(stamp: InstalledStamp | null | undefined): boolean {
  return stamp?.features?.email?.enabled === true;
}

/** Is this feature record both switched on AND actually usable?
 *
 *  Intent alone is not enough to send: an `enabled: true` record with no endpoint or sender
 *  would produce an executor that believes it can send and then fails at the wire. Callers
 *  wiring the executor use this; callers reporting operator intent use `isEmailEnabled`.
 *
 *  The strict `=== true` lives here rather than at each call site because the stamp is JSON
 *  read from a table — the declared type says boolean, the wire can say anything. */
export function isEmailFeatureUsable(f: EmailFeatureStamp | null | undefined): boolean {
  // `typeof === "string"` and not merely `!== ""`: the declared type says string, but a
  // hand-edited or partially-migrated stamp can carry undefined or null here, and both
  // compare unequal to "". Letting those through writes `M8T_ACS_SENDER: null` into an agent
  // definition — which serialises as null, is rejected by the version POST, and (because
  // null !== undefined in the reconcile's comparison) then retries on every converge for ever.
  return (
    f?.enabled === true &&
    typeof f.acsEndpoint === "string" &&
    f.acsEndpoint !== "" &&
    typeof f.acsSender === "string" &&
    f.acsSender !== ""
  );
}

export function stampToEntity(s: InstalledStamp): Record<string, unknown> {
  return {
    partitionKey: PK,
    rowKey: RK,
    platformVersion: s.platformVersion,
    updatedAt: s.updatedAt,
    lastResult: s.lastResult,
    value: JSON.stringify(s),
  };
}

export function entityToStamp(e: Record<string, unknown>): InstalledStamp | null {
  const raw = e.value;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw) as InstalledStamp;
  } catch {
    return null;
  }
}
